import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';

interface UseFullscreenControlOptions {
  map: maplibregl.Map | null;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const useFullscreenControl = ({ map, containerRef }: UseFullscreenControlOptions) => {
  const controlRef = useRef<any>(null);
  const isFullscreenRef = useRef(false);

  useEffect(() => {
    if (!map || !containerRef.current) return;

    // Create custom fullscreen control
    class FullscreenControl {
      container!: HTMLDivElement;
      button!: HTMLButtonElement;

      onAdd(_map: maplibregl.Map) {
        this.container = document.createElement('div');
        this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';

        this.button = document.createElement('button');
        this.button.className = 'maplibregl-ctrl-icon maplibregl-ctrl-fullscreen';
        this.button.type = 'button';
        this.button.title = 'Toggle fullscreen';
        this.button.setAttribute('aria-label', 'Toggle fullscreen');

        // Apply consistent styling with other MapLibre controls
        this.button.style.width = '29px';
        this.button.style.height = '29px';
        this.button.style.padding = '0';
        this.button.style.cursor = 'pointer';
        this.button.style.display = 'flex';
        this.button.style.alignItems = 'center';
        this.button.style.justifyContent = 'center';

        this.button.innerHTML = this.getIcon(false);

        this.button.addEventListener('click', () => {
          this.toggleFullscreen();
        });

        // Listen for fullscreen change events
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);

        this.container.appendChild(this.button);
        return this.container;
      }

      onRemove() {
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
        this.container.parentNode?.removeChild(this.container);
      }

      handleFullscreenChange = () => {
        const isFullscreen = !!(
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement
        );
        isFullscreenRef.current = isFullscreen;
        this.button.innerHTML = this.getIcon(isFullscreen);
        this.button.title = isFullscreen ? 'Exit fullscreen' : 'Toggle fullscreen';
      };

      getIcon(isFullscreen: boolean) {
        if (isFullscreen) {
          // Material Design exit fullscreen icon
          return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
            <path fill="currentColor" d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
          </svg>`;
        } else {
          // Material Design enter fullscreen icon
          return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
            <path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zM17 7h-3V5h5v5h-2V7zm-3 12v-2h3v-3h2v5h-5z"/>
          </svg>`;
        }
      }

      toggleFullscreen() {
        const element = containerRef.current;
        if (!element) return;

        if (!isFullscreenRef.current) {
          // Enter fullscreen
          if (element.requestFullscreen) {
            element.requestFullscreen();
          } else if ((element as any).webkitRequestFullscreen) {
            (element as any).webkitRequestFullscreen();
          } else if ((element as any).mozRequestFullScreen) {
            (element as any).mozRequestFullScreen();
          } else if ((element as any).msRequestFullscreen) {
            (element as any).msRequestFullscreen();
          }
        } else {
          // Exit fullscreen
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if ((document as any).webkitExitFullscreen) {
            (document as any).webkitExitFullscreen();
          } else if ((document as any).mozCancelFullScreen) {
            (document as any).mozCancelFullScreen();
          } else if ((document as any).msExitFullscreen) {
            (document as any).msExitFullscreen();
          }
        }
      }
    }

    // Remove existing control if it exists
    if (controlRef.current) {
      map.removeControl(controlRef.current);
    }

    // Add new control
    const control = new FullscreenControl();
    controlRef.current = control;
    map.addControl(control as any, 'top-right');

    return () => {
      if (map && controlRef.current) {
        try {
          map.removeControl(controlRef.current);
        } catch (e) {
          // Map might be destroyed
        }
      }
    };
  }, [map, containerRef]);
};